
import React, { useState } from 'react';
import { User } from '../types';
import { historyService } from '../services/historyService';
import { authService } from '../services/authService';
import { Info, LogOut, ChevronRight, Database, Globe, Cpu, CheckCircle2, Lock, Loader2 } from 'lucide-react';

interface Props {
  user: User | null;
  onLogout: () => void;
  onClearHistory: () => void;
}

const SystemSettings: React.FC<Props> = ({ user, onLogout, onClearHistory }) => {
  const [clearing, setClearing] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [pwdLoading, setPwdLoading] = useState(false);
  const [pwdData, setPwdData] = useState({ old: '', new: '', confirm: '' });
  const [pwdStatus, setPwdStatus] = useState({ type: '', msg: '' });

  const handleClearData = async () => {
    if (!user) return;
    if (!confirm('确定要清空数据库中存储的所有历史身份记录吗？此操作不可撤销。')) return;
    
    setClearing(true);
    const success = await historyService.clearHistory(user.username);
    if (success) {
      onClearHistory();
      alert('历史记录已成功清空');
    }
    setClearing(false);
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    if (pwdData.new !== pwdData.confirm) {
      setPwdStatus({ type: 'error', msg: '两次输入的新密码不一致' });
      return;
    }
    setPwdLoading(true);
    try {
      const res = await authService.changePassword(user.username, pwdData.old, pwdData.new);
      if (res.success) {
        setPwdStatus({ type: 'success', msg: '密码修改成功' });
        setPwdData({ old: '', new: '', confirm: '' });
        setTimeout(() => setShowPasswordForm(false), 2000);
      } else {
        setPwdStatus({ type: 'error', msg: res.message });
      }
    } catch (e) {
      setPwdStatus({ type: 'error', msg: '请求失败' });
    } finally {
      setPwdLoading(false);
    }
  };

  const SettingItem = ({ icon: Icon, title, desc, action, danger = false, rightElement = null }: any) => (
    <div className="flex items-center justify-between p-6 bg-white border border-slate-100 rounded-3xl hover:border-blue-100 hover:shadow-sm transition-all group">
      <div className="flex items-center gap-4">
        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${danger ? 'bg-red-50 text-red-500' : 'bg-slate-50 text-slate-500 group-hover:bg-blue-50 group-hover:text-blue-600'}`}>
          <Icon size={22} />
        </div>
        <div>
          <h4 className="font-bold text-slate-800 text-sm">{title}</h4>
          <p className="text-xs text-slate-600 mt-0.5">{desc}</p>
        </div>
      </div>
      <div className="flex items-center gap-4">
        {rightElement}
        <button 
          onClick={action}
          disabled={clearing}
          className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${danger ? 'text-red-500 hover:bg-red-50' : 'bg-slate-50 text-slate-600 hover:bg-blue-600 hover:text-white'}`}
        >
          {danger ? (clearing ? '正在清空...' : '立即执行') : <ChevronRight size={18} />}
        </button>
      </div>
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500 pb-20">
      <section>
        <h3 className="text-[10px] font-black text-slate-600 uppercase tracking-[0.2em] mb-4 ml-2">账户管理</h3>
        {user ? (
          <div className="bg-slate-900 rounded-[2.5rem] p-8 text-white relative overflow-hidden shadow-2xl shadow-slate-200">
            <div className="absolute top-0 right-0 w-64 h-64 bg-blue-600/20 rounded-full -mr-32 -mt-32 blur-3xl"></div>
            <div className="flex items-center justify-between relative z-10">
              <div className="flex items-center gap-6">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-3xl flex items-center justify-center text-3xl font-black">
                  {user.username.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h2 className="text-2xl font-black">{user.username}</h2>
                  <div className="flex flex-col gap-1 mt-2">
                    <p className="text-xs text-blue-300 font-mono">{user.email}</p>
                    <span className="w-fit px-2 py-0.5 bg-blue-500/20 border border-blue-500/30 rounded text-[10px] font-bold uppercase text-blue-300">Pro 会员</span>
                  </div>
                </div>
              </div>
              <button onClick={onLogout} className="p-4 bg-white/10 hover:bg-red-500/20 text-white hover:text-red-400 rounded-2xl transition-all border border-white/5">
                <LogOut size={20} />
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-white p-8 rounded-[2.5rem] border-2 border-dashed border-slate-200 text-center">
            <p className="text-slate-600 font-bold">请先登录以查看账户详情</p>
          </div>
        )}
      </section>

      <section className="space-y-3">
        <h3 className="text-[10px] font-black text-slate-600 uppercase tracking-[0.2em] mb-4 ml-2">安全中心</h3>
        <SettingItem 
          icon={Lock} 
          title="修改账户密码" 
          desc="定期更新密码有助于保护您的账号安全"
          action={() => setShowPasswordForm(!showPasswordForm)}
        />
        {showPasswordForm && (
          <div className="p-8 bg-white border border-blue-100 rounded-3xl shadow-lg animate-in slide-in-from-top-4 duration-300">
            <form onSubmit={handleUpdatePassword} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-slate-600 uppercase tracking-widest ml-1">当前密码</label>
                  <input type="password" required value={pwdData.old} onChange={e => setPwdData({...pwdData, old: e.target.value})} className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-xl text-sm focus:outline-none focus:border-blue-400" />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-slate-600 uppercase tracking-widest ml-1">新密码</label>
                  <input type="password" required value={pwdData.new} onChange={e => setPwdData({...pwdData, new: e.target.value})} className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-xl text-sm focus:outline-none focus:border-blue-400" />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <label className="text-[10px] font-bold text-slate-600 uppercase tracking-widest ml-1">确认新密码</label>
                  <input type="password" required value={pwdData.confirm} onChange={e => setPwdData({...pwdData, confirm: e.target.value})} className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-xl text-sm focus:outline-none focus:border-blue-400" />
                </div>
              </div>
              {pwdStatus.msg && <div className={`text-xs font-bold p-3 rounded-xl ${pwdStatus.type === 'success' ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'}`}>{pwdStatus.msg}</div>}
              <button disabled={pwdLoading} className="px-6 py-3 bg-slate-900 text-white rounded-xl text-xs font-bold hover:bg-slate-800 transition-all flex items-center gap-2">
                {pwdLoading && <Loader2 size={14} className="animate-spin" />} 更新密码
              </button>
            </form>
          </div>
        )}
      </section>

      <section className="space-y-3">
        <h3 className="text-[10px] font-black text-slate-600 uppercase tracking-[0.2em] mb-4 ml-2">数据管理</h3>
        <SettingItem 
          icon={Database} 
          title="清空个人历史" 
          desc="永久删除您在云端数据库中同步的所有身份生成历史"
          danger
          action={handleClearData}
        />
      </section>

      <section className="space-y-3">
        <h3 className="text-[10px] font-black text-slate-600 uppercase tracking-[0.2em] mb-4 ml-2">技术状态</h3>
        <div className="bg-white p-8 rounded-[2.5rem] border border-slate-200 shadow-sm">
           <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center">
                  <Globe size={22} />
                </div>
                <div>
                  <p className="text-[10px] font-bold text-slate-600 uppercase tracking-widest">服务节点</p>
                  <p className="text-sm font-bold text-slate-800">Edge Global</p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-indigo-50 text-indigo-600 rounded-2xl flex items-center justify-center">
                  <Cpu size={22} />
                </div>
                <div>
                  <p className="text-[10px] font-bold text-slate-600 uppercase tracking-widest">AI 内核</p>
                  <div className="flex items-center gap-2 text-sm font-bold text-slate-800">
                    Gemini 3 系列
                    <span className="flex items-center gap-1 text-[10px] text-green-600 bg-green-50 px-2 py-0.5 rounded-md border border-green-100">
                      <CheckCircle2 size={10} /> 在线
                    </span>
                  </div>
                </div>
              </div>
           </div>
        </div>
      </section>
    </div>
  );
};

export default SystemSettings;
